import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Select, Wordmark } from '@keobook/ui';
import {
  COUNTRY_OPTIONS,
  CURRENCY_BY_COUNTRY,
  ENTITY_TYPES_BY_COUNTRY,
  type ClientCreate,
  type CountryCode,
  type EntityType,
} from '@keobook/shared';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { US_STATES } from '../lib/us-states';

type Kind = 'business' | 'individual';

/**
 * Client-side mirror of the server's US tax-id Zod regex
 * (packages/shared/src/schemas.ts). Surfaced as an inline field error so
 * users see "EIN must be 9 digits" before submitting, instead of a generic
 * "Invalid request body" round-tripped from the API. Only US clients have a
 * strict format — non-US tax IDs are optional free text and never validated.
 */
function isValidUsTaxId(taxId: string, kind: Kind): boolean {
  const t = taxId.trim();
  if (kind === 'business') return /^\d{2}-?\d{7}$/.test(t);
  return /^\d{3}-?\d{2}-?\d{4}$/.test(t);
}

export function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [kind, setKind] = useState<Kind>('business');
  const [country, setCountry] = useState<CountryCode>('US');
  const [entityType, setEntityType] = useState<EntityType>('c_corp');
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [stateCode, setStateCode] = useState('CA');
  const [fiscalYearStart, setFiscalYearStart] = useState('01-01');
  const [basis, setBasis] = useState<'cash' | 'accrual'>('cash');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isUs = country === 'US';
  const currency = CURRENCY_BY_COUNTRY[country];
  const entityOptions = ENTITY_TYPES_BY_COUNTRY[country];

  // Switching country changes which entity types are valid, the currency, and
  // whether US-only fields apply. Reset the entity type to the first option for
  // the new jurisdiction so we never submit a value that isn't in its list.
  function onCountryChange(next: CountryCode) {
    setCountry(next);
    setEntityType(ENTITY_TYPES_BY_COUNTRY[next][0]!.value);
  }

  const usTaxIdError =
    isUs && taxId.trim().length > 0 && !isValidUsTaxId(taxId, kind)
      ? kind === 'business'
        ? 'EIN must be 9 digits in 12-3456789 format'
        : 'SSN/ITIN must be 9 digits in 123-45-6789 format'
      : undefined;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const trimmedTaxId = taxId.trim();
    const common = {
      legalName: legalName.trim(),
      displayName: displayName.trim() || legalName.trim(),
      country,
      currency,
      // US requires a tax ID; non-US is optional free text — omit when blank.
      ...(trimmedTaxId ? { taxId: trimmedTaxId } : {}),
      // US state applies to US clients only.
      ...(isUs ? { stateCode } : {}),
    };

    const payload: ClientCreate =
      kind === 'business'
        ? { kind: 'business', ...common, fiscalYearStart, basis, entityType }
        : { kind: 'individual', ...common };

    try {
      await api.onboard(payload);
      await refresh();
      navigate('/engagement-letter', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Onboarding failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="kb-auth-page">
      <div className="kb-auth-container" style={{ maxWidth: 520 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Wordmark size="lg" tagline="Tell us who we're keeping books for" />
        </div>

        <Card size="xl">
          <CardTitle>Set up your engagement</CardTitle>
          <CardSubtitle>This determines your chart of accounts and tax-prep package.</CardSubtitle>

          <form onSubmit={onSubmit} noValidate>
            <Field
              label="Country"
              required
              hint={`Your books will be kept in ${currency}. This can't be changed later.`}
            >
              <Select
                value={country}
                onChange={(e) => onCountryChange(e.currentTarget.value as CountryCode)}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </Field>

            {!isUs && (
              <p style={{ fontSize: 13, color: '#7a7870', marginTop: -6, marginBottom: 14 }}>
                International engagements are bookkeeping-only — invoices and reports in {currency},
                without US or local tax-filing features. Connect-a-bank is US-only; you'll upload
                statements instead.
              </p>
            )}

            <Field label="Engagement kind" required>
              <div className="kb-radiopair">
                <label className={`kb-radio ${kind === 'business' ? 'kb-radio--selected' : ''}`}>
                  <input
                    type="radio"
                    name="kind"
                    value="business"
                    checked={kind === 'business'}
                    onChange={() => setKind('business')}
                  />
                  <span className="kb-radio__title">Business</span>
                  <span className="kb-radio__hint">Pick your entity type below</span>
                </label>
                <label className={`kb-radio ${kind === 'individual' ? 'kb-radio--selected' : ''}`}>
                  <input
                    type="radio"
                    name="kind"
                    value="individual"
                    checked={kind === 'individual'}
                    onChange={() => setKind('individual')}
                  />
                  <span className="kb-radio__title">Individual</span>
                  <span className="kb-radio__hint">
                    {isUs ? 'Personal accounting · Form 1040' : 'Personal accounting'}
                  </span>
                </label>
              </div>
            </Field>

            {kind === 'business' && (
              <Field label="Business entity type" required>
                <Select
                  value={entityType}
                  onChange={(e) => setEntityType(e.currentTarget.value as EntityType)}
                  required
                >
                  {entityOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field label={kind === 'business' ? 'Legal business name' : 'Legal name'} required>
              <Input
                value={legalName}
                onChange={(e) => setLegalName(e.currentTarget.value)}
                placeholder={kind === 'business' ? 'Acme Co LLC' : 'Jane Q. Public'}
                required
              />
            </Field>

            <Field
              label="Display name"
              hint="What we'll call you in the app. Defaults to your legal name."
            >
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                placeholder={kind === 'business' ? 'Acme Co' : 'Jane'}
              />
            </Field>

            {isUs ? (
              <Field
                label={kind === 'business' ? 'EIN' : 'SSN or ITIN'}
                required
                hint={kind === 'business' ? 'Format: 12-3456789 (9 digits)' : 'Format: 123-45-6789'}
                error={usTaxIdError}
              >
                <Input
                  value={taxId}
                  onChange={(e) => setTaxId(e.currentTarget.value)}
                  placeholder={kind === 'business' ? '12-3456789' : '123-45-6789'}
                  required
                  inputMode="numeric"
                  autoComplete="off"
                  invalid={!!usTaxIdError}
                />
              </Field>
            ) : (
              <Field
                label={kind === 'business' ? 'Business / tax number' : 'Tax number'}
                hint="Optional. Your local company or tax reference number, if you have one."
              >
                <Input
                  value={taxId}
                  onChange={(e) => setTaxId(e.currentTarget.value)}
                  placeholder="Optional"
                  autoComplete="off"
                />
              </Field>
            )}

            {isUs && (
              <Field label="State" required>
                <Select value={stateCode} onChange={(e) => setStateCode(e.currentTarget.value)}>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {kind === 'business' && (
              <>
                <Field
                  label="Fiscal year start"
                  hint="MM-DD. Calendar year customers leave this as 01-01."
                >
                  <Input
                    value={fiscalYearStart}
                    onChange={(e) => setFiscalYearStart(e.currentTarget.value)}
                    placeholder="01-01"
                  />
                </Field>

                <Field label="Accounting basis" required>
                  <Select
                    value={basis}
                    onChange={(e) => setBasis(e.currentTarget.value as 'cash' | 'accrual')}
                  >
                    <option value="cash">Cash basis (simpler)</option>
                    <option value="accrual">Accrual basis (full GAAP set)</option>
                  </Select>
                </Field>
              </>
            )}

            {error && (
              <div role="alert" className="kb-form-error">
                {error}
              </div>
            )}

            <Button block type="submit" disabled={submitting}>
              {submitting ? 'Setting up…' : 'Continue to engagement letter'}
            </Button>
          </form>
        </Card>
      </div>
    </main>
  );
}
